import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import companiesApi from "../../api/companiesApi";
import companyBranchesApi from "../../api/companyBranchesApi";
import "./header.css";

const analyticsSurveySlugs = new Set([
  "hra",
  "hb",
  "healthBurnout",
  "estresBurnout",
  "stress",
  "nom035",
  "nom035SecI",
  "generalHealtQuestionarie",
  "generalHealtQuestionnaire",
  "generalHealthQuestionarie",
  "generalHealthQuestionnaire",
  "results",
]);

function getScopeIdFromPath(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "analytics") {
    return analyticsSurveySlugs.has(parts[1]) && parts[2] !== "global"
      ? parts[2]
      : null;
  }

  const match = pathname.match(
    /\/(?:HRA|hra|healthBurnout|estresBurnout|stress|seguimiento|nom035|nom035SecI|generalHealtQuestionarie|generalHealtQuestionnaire|generalHealthQuestionarie|generalHealthQuestionnaire|results)\/([a-zA-Z0-9-]+)/
  );
  return match?.[1] || null;
}

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const [companyName, setCompanyName] = useState("Deli Life Wellness");
  const [logoUrl, setLogoUrl] = useState("/logo512.png");
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuScopes, setMenuScopes] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);

  // Decode token synchronously for render decisions
  let role = null;
  let tokenCompanyIds = [];
  let tokenBranchIds = [];
  try {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      role = decoded.role;
      tokenCompanyIds = decoded.companies || [];
      tokenBranchIds = decoded.branches || [];
    }
  } catch (_) {}

  useEffect(() => {
    const token = localStorage.getItem("token");
    let isSuperAdmin = false;
    let decodedCompanies = [];
    let decodedBranches = [];
    let scopeId = location.state?.branchId || location.state?.companyId || null;

    if (token) {
      try {
        const decoded = jwtDecode(token);
        isSuperAdmin = decoded.role === "super admin";
        decodedCompanies = decoded.companies || [];
        decodedBranches = decoded.branches || [];
      } catch (err) {
        console.error("Error al decodificar token:", err);
      }
    }

    const pathScopeId = getScopeIdFromPath(location.pathname);
    if (!scopeId && pathScopeId) {
      scopeId = pathScopeId;
    }

    if (scopeId) {
      const loadScopeBrand = decodedBranches.map(String).includes(String(scopeId))
        ? companyBranchesApi.getById(scopeId)
        : companiesApi.getCompanyById(scopeId).catch(() => companyBranchesApi.getById(scopeId));

      loadScopeBrand
        .then(scope => {
          setCompanyName(scope.name || "Deli Life Wellness");
          setLogoUrl(scope.logoUrl || "/logo512.png");
        })
        .catch(() => {
          setCompanyName("Deli Life Wellness");
          setLogoUrl("/logo512.png");
        });
      return;
    }

    if (token) {
      try {
        const firstBranch = decodedBranches[0];
        const firstCompany = decodedCompanies[0];
        const firstScopeId = firstBranch || firstCompany;
        if (!isSuperAdmin && firstScopeId) {
          const loadScopeBrand = firstBranch
            ? companyBranchesApi.getById(firstBranch)
            : companiesApi.getCompanyById(firstCompany);

          loadScopeBrand
            .then(scope => {
              setCompanyName(scope.name || "Deli Life Wellness");
              setLogoUrl(scope.logoUrl || "/logo512.png");
            })
            .catch(() => {
              setCompanyName("Deli Life Wellness");
              setLogoUrl("/logo512.png");
            });
          return;
        }
      } catch (err) {
        console.error("Error al decodificar token:", err);
      }
    }

    if (isSuperAdmin) {
      setCompanyName("Deli Life Wellness");
      setLogoUrl("/logo512.png");
    }
  }, [location.pathname, location.state?.branchId, location.state?.companyId]);

  // Close hamburger on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMenuToggle = async () => {
    const opening = !menuOpen;
    setMenuOpen(opening);
    // Lazy-load assigned scope names on first open.
    if (opening && menuScopes.length === 0 && (tokenBranchIds.length > 0 || tokenCompanyIds.length > 0)) {
      setMenuLoading(true);
      try {
        const scopes = tokenBranchIds.length > 0
          ? await Promise.all(tokenBranchIds.map(id => companyBranchesApi.getById(id)))
          : await Promise.all(tokenCompanyIds.map(id => companiesApi.getCompanyById(id)));
        setMenuScopes(scopes);
      } catch (err) {
        console.error("Error cargando sucursales del menú:", err);
      } finally {
        setMenuLoading(false);
      }
    }
  };

  const handleSelectScope = (scopeId) => {
    setMenuOpen(false);
    const isBranchScope = tokenBranchIds.map(String).includes(String(scopeId));
    navigate("/dashboard", {
      state: {
        companyId: scopeId,
        branchId: isBranchScope ? scopeId : undefined,
      }
    });
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const questionnaireAndResultPrefixes = [
    "/hra", "/HRA", "/healthBurnout", "/estresLaboral", "/estres",
    "/estresBurnout", "/stress", "/seguimiento", "/nom035", "/nom035SecI",
    "/generalHealtQuestionarie", "/generalHealtQuestionnaire",
    "/generalHealthQuestionarie", "/generalHealthQuestionnaire",
    "/cgs", "/hb", "/users",
  ];

  const isQuestionnaire = questionnaireAndResultPrefixes.some(prefix =>
    location.pathname.startsWith(prefix)
  );

  const linkableRoutes = [
    "/analytics", "/company", "/configuracion",
    "/hraConfiguracion", "/superAdminConfig", "/adminConfig",
  ];
  const isLinkable = linkableRoutes.some(route => location.pathname.startsWith(route));
  const pathScopeId = getScopeIdFromPath(location.pathname);
  const activeHeaderScopeId =
    location.state?.branchId ||
    location.state?.companyId ||
    pathScopeId ||
    tokenBranchIds[0] ||
    tokenCompanyIds[0] ||
    null;
  const activeHeaderScopeIsBranch = tokenBranchIds
    .map(String)
    .includes(String(activeHeaderScopeId));
  const headerHomePath = role === "admin" ? "/dashboard" : "/companies";
  const headerHomeState = role === "admin" && activeHeaderScopeId
    ? {
        companyId: activeHeaderScopeId,
        branchId: activeHeaderScopeIsBranch ? activeHeaderScopeId : undefined,
      }
    : undefined;

  return (
    <header className="header">
      {isLinkable ? (
        <Link
          to={headerHomePath}
          state={headerHomeState}
          className="header-text"
        >
          {companyName}
        </Link>
      ) : (
        <div className="header-text">{companyName}</div>
      )}

      {/* Hamburger menu: only for admins on non-questionnaire pages */}
      {!isQuestionnaire && role === "admin" && (
        <div className="hamburger-wrapper" ref={menuRef}>
          <button
            className="hamburger-button"
            onClick={handleMenuToggle}
            aria-label="Menú"
            aria-expanded={menuOpen}
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>

          {menuOpen && (
            <div className="hamburger-dropdown">
              <div className="hamburger-section-title">
                {tokenBranchIds.length > 0 ? "Sucursales" : "Empresas"}
              </div>
              {menuLoading ? (
                <span className="hamburger-loading">Cargando...</span>
              ) : menuScopes.length > 0 ? (
                menuScopes.map(scope => (
                  <button
                    key={scope._id}
                    className="hamburger-item"
                    onClick={() => handleSelectScope(scope._id)}
                  >
                    {scope.name}
                  </button>
                ))
              ) : (
                <span className="hamburger-loading">
                  {tokenBranchIds.length > 0 ? "Sin sucursales asignadas" : "Sin empresas asignadas"}
                </span>
              )}
              <div className="hamburger-divider" />
              <button
                className="hamburger-item"
                onClick={() => { setMenuOpen(false); navigate('/2fa/configuracion'); }}
              >
                Autenticación 2FA
              </button>
              <div className="hamburger-divider" />
              <button className="hamburger-item hamburger-logout" onClick={handleLogout}>
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      )}

      {/* super admin and root user have logout in the sidebar — nothing shown here */}

      <div className="header-logo-container">
        <img src={logoUrl} alt="Logo" className="logo" />
      </div>
    </header>
  );
};

export default Header;
